'use client';

import { Form, Nimbus3Scope } from '@gartner-digital-markets/nimbus-v3-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { identifyVisitorByEmail } from '../browser/identity';
import { createGatewaySubmitAction } from '../browser/submission';
import {
  type ClientDataContextProps,
  type FormPayload,
  type FormTracking,
  type OnServerLog,
  type SubmitFormResult,
  createEmailSchema,
} from '../core';
import {
  DEFAULT_SECOND_BUTTON_TEXT,
  DEFAULT_STEP2_SUBTITLE,
  DefaultQualificationStep,
  DEFAULT_QUALIFICATION_OPTIONS as SHARED_DEFAULT_QUALIFICATION_OPTIONS,
  createQualificationStepDefaultValues,
  createQualificationStepSchema,
  resolveQualificationConfig,
} from './components/DefaultQualificationStep';
import { EmailField } from './components/EmailField';
import { type CardBackground, FormCard } from './components/FormCard';
import { FormFooter } from './components/FormFooter';
import { FormHeadline } from './components/FormHeadline';
import { SubmissionErrorMessage } from './components/SubmissionErrorMessage';
import { SubmitButton } from './components/SubmitButton';
import { SuccessScreen } from './components/SuccessScreen';
import { useEmailStorage } from './hooks/useEmailStorage';
import { useFormAnalytics } from './hooks/useFormAnalytics';
import { useFormSubmission } from './hooks/useFormSubmission';
import { useMultiStepForm } from './hooks/useMultiStepForm';

export const DEFAULT_QUALIFICATION_OPTIONS =
  SHARED_DEFAULT_QUALIFICATION_OPTIONS;

const emailStepSchema = z.object({
  emailAddress: createEmailSchema(),
});

export type InlineFormTemplate =
  | 'INLINE_MINIMAL_NEW'
  | 'INLINE_CARD'
  | 'INLINE_MINIMAL'
  | 'STACKED_MINIMAL';

type FieldLayout = 'inline' | 'stacked';
type ButtonStyle = 'primary' | 'preferred';

interface TemplateConfig {
  className: string;
  fieldLayout: FieldLayout;
  buttonStyle: ButtonStyle;
  submitButtonStyle?: string;
}

const TEMPLATE_STYLES: Record<InlineFormTemplate, TemplateConfig> = {
  INLINE_MINIMAL_NEW: {
    className: 'gap-0 p-6',
    fieldLayout: 'inline',
    buttonStyle: 'primary',
    submitButtonStyle: 'w-full md:w-auto',
  },
  INLINE_CARD: {
    className: 'gap-0 p-6',
    fieldLayout: 'stacked',
    buttonStyle: 'preferred',
    submitButtonStyle: 'w-full md:w-auto',
  },
  INLINE_MINIMAL: {
    className: 'gap-0 p-6',
    fieldLayout: 'stacked',
    buttonStyle: 'primary',
    submitButtonStyle: 'w-full md:w-auto',
  },
  STACKED_MINIMAL: {
    className: 'gap-0 p-6',
    fieldLayout: 'stacked',
    buttonStyle: 'primary',
    submitButtonStyle: 'w-full',
  },
};

export interface InlineFormProps {
  formId: string;
  lang?: string;
  formTemplate?: InlineFormTemplate;
  isSingleStep?: boolean;
  isTimeline?: boolean;
  isPricing?: boolean;
  isCompanySize?: boolean;
  isFreeTrial?: boolean;
  headline?: string;
  subHeading?: string;
  buttonText?: string;
  step2Subtitle?: string;
  step2QuestionLabel?: string;
  secondButtonText?: string;
  qualificationOptions?: readonly string[] | null;
  successHeadline?: string;
  successMessage?: string;
  successIcon?: ReactNode;
  width?: string;
  showRuler?: boolean;
  /** When true, renders without Card wrapper (no rounded corners, shadow, or background) */
  cardless?: boolean;
  contextProps?: ClientDataContextProps;
  tracking?: FormTracking;
  gatewayUrl?: string;
  submitAction?: (payload: FormPayload) => Promise<SubmitFormResult>;
  onServerLog?: OnServerLog;
  termsUrl?: string;
  privacyUrl?: string;
  cardBackground?: CardBackground;
}

export interface InlineFormBrandConfig {
  defaultTracking?: FormTracking;
  successIcon?: ReactNode;
  termsUrl?: string;
  privacyUrl?: string;
  submissionFailedMessageKey?: string;
}

interface SharedInlineFormProps extends InlineFormProps {
  brandConfig: InlineFormBrandConfig;
}

/**
 * Renders an inline email capture form with optional two-step qualification flow.
 *
 * This component displays an embedded form for lead generation that collects
 * email addresses and optional qualification data. Supports multiple visual
 * templates and configurable field layouts.
 *
 * @example
 * ```tsx
 * import { InlineForm } from 'bx-forms/eas/capterra';
 *
 * <InlineForm
 *   formId="contentful-form-id"
 *   headline="Get the best software recommendations"
 *   isSingleStep={false}
 * />
 * ```
 */
export const InlineForm = ({
  formId,
  lang,
  formTemplate = 'INLINE_MINIMAL_NEW',
  isSingleStep = false,
  isTimeline = false,
  isPricing = false,
  isCompanySize = false,
  isFreeTrial = false,
  headline = 'Top-rated software of 2026',
  subHeading = "Fill out the form and we'll send a list of the top-rated software based on real user reviews directly to your inbox.",
  buttonText = 'Send me the list',
  step2Subtitle = DEFAULT_STEP2_SUBTITLE,
  step2QuestionLabel,
  secondButtonText = DEFAULT_SECOND_BUTTON_TEXT,
  qualificationOptions,
  successHeadline = 'Thank you, enjoy!',
  successMessage = 'Your list will be delivered to your inbox shortly.',
  successIcon,
  contextProps,
  tracking,
  width,
  showRuler = false,
  cardless = false,
  gatewayUrl = '/bx-capterra-api/bx-forms/submit',
  submitAction,
  onServerLog,
  termsUrl,
  privacyUrl,
  cardBackground,
  brandConfig,
}: SharedInlineFormProps) => {
  const mergedTracking = useMemo(
    () => ({ ...(brandConfig.defaultTracking ?? {}), ...tracking }),
    [brandConfig.defaultTracking, tracking]
  );
  const portalBridgeAttributes = useMemo(() => {
    const brand = brandConfig.defaultTracking?.brand;

    if (!brand) {
      return undefined;
    }

    return {
      'data-bx-forms-brand': brand,
    };
  }, [brandConfig.defaultTracking?.brand]);

  const resolvedSubmitAction = useMemo(
    () => submitAction ?? createGatewaySubmitAction(gatewayUrl),
    [submitAction, gatewayUrl]
  );

  const templateConfig = TEMPLATE_STYLES[formTemplate];
  const isStacked = templateConfig.fieldLayout === 'stacked';
  const successStep = isSingleStep ? 1 : 2;
  const resolvedTermsUrl = termsUrl ?? brandConfig.termsUrl;
  const resolvedPrivacyUrl = privacyUrl ?? brandConfig.privacyUrl;
  const qualificationStepConfig = useMemo(
    () =>
      resolveQualificationConfig({
        lang,
        isTimeline,
        isPricing,
        isCompanySize,
        isFreeTrial,
        label: step2QuestionLabel,
        qualificationOptions,
      }),
    [
      lang,
      isTimeline,
      isPricing,
      isCompanySize,
      isFreeTrial,
      step2QuestionLabel,
      qualificationOptions,
    ]
  );
  const qualificationStepSchema = useMemo(
    () => createQualificationStepSchema(qualificationStepConfig.fieldName),
    [qualificationStepConfig.fieldName]
  );
  const qualificationStepDefaultValues = useMemo(
    () =>
      createQualificationStepDefaultValues(qualificationStepConfig.fieldName),
    [qualificationStepConfig.fieldName]
  );

  const { step, formData, nextStep } = useMultiStepForm({
    totalSteps: successStep + 1,
  });
  const { submitToApi, submissionErrorMessage, isLoading } = useFormSubmission({
    formId,
    tracking: mergedTracking,
    contextProps,
    templateName: 'eas-comparison-flow',
    submitAction: resolvedSubmitAction,
  });
  const analytics = useFormAnalytics(formId);
  const { setStoredEmail, getStoredEmail } = useEmailStorage();
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!formId) {
      analytics.trackFormFailedToDisplay();
    }
  }, [formId, analytics]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const isPrefilled = !!getStoredEmail();
            analytics.trackFormOpen();
            analytics.trackFormExposeServer({
              formTemplate,
              formKind: 'inline',
              brand: mergedTracking.brand,
              placement: contextProps?.pagePlacement,
              page_type: contextProps?.pageType,
              page_path: window.location.pathname,
              eloqua_campaign: mergedTracking.eloquaCampaign,
            });
            if (isPrefilled) {
              analytics.trackFormPrefilled();
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => observer.disconnect();
  }, [
    analytics,
    contextProps,
    formTemplate,
    getStoredEmail,
    mergedTracking.brand,
    mergedTracking.eloquaCampaign,
  ]);

  useEffect(() => {
    if (!isSingleStep && step === 1) {
      analytics.trackMicroDataQuestionDisplayed();
    }
  }, [analytics, isSingleStep, step]);

  const emailForm = useForm({
    resolver: zodResolver(emailStepSchema),
    defaultValues: { emailAddress: getStoredEmail() || '' },
    mode: 'onBlur',
  });

  const qualificationForm = useForm<Record<string, unknown>>({
    resolver: zodResolver(qualificationStepSchema),
    defaultValues: qualificationStepDefaultValues,
    mode: 'onBlur',
    shouldUnregister: true,
  });

  useEffect(() => {
    qualificationForm.reset(qualificationStepDefaultValues);
  }, [qualificationForm, qualificationStepDefaultValues]);

  const handleSubmissionFailure = (result: SubmitFormResult) => {
    onServerLog?.(
      JSON.stringify({
        messageKey:
          brandConfig.submissionFailedMessageKey ??
          'bx_forms_submission_failed',
        message: `Form submission failed : ${JSON.stringify(result)}`,
        formId,
        contextProps,
      })
    );
  };

  const handleEmailSubmit = async (data: z.infer<typeof emailStepSchema>) => {
    const result = await submitToApi({
      ...formData,
      ...data,
      hasSecondPage: !isSingleStep,
    });

    if (result.success) {
      setStoredEmail(data.emailAddress);
      identifyVisitorByEmail(data.emailAddress);
      analytics.trackFormSubmitted(buttonText);
      nextStep(data);
      return;
    }

    handleSubmissionFailure(result);
  };

  const handleQualificationSubmit = async (data: Record<string, unknown>) => {
    const result = await submitToApi({
      ...formData,
      ...data,
      hasSecondPage: false,
    });

    if (result.success) {
      analytics.trackMicroDataQuestionSubmitted();
      nextStep(data);
      return;
    }

    handleSubmissionFailure(result);
  };

  const handleError = (currentStep: 'email' | 'qualification') => {
    if (currentStep === 'email') {
      analytics.trackFormFailed();
      return;
    }

    analytics.trackMicroDataQuestionFailed();
  };

  const widthStyle = width ? { maxWidth: width } : undefined;

  if (step === successStep) {
    return (
      <Nimbus3Scope
        portalBridgeAttributes={portalBridgeAttributes}
        style={{ background: 'transparent' }}
      >
        <FormCard
          className={templateConfig.className}
          style={widthStyle}
          cardBackground={cardBackground}
          cardless={cardless}
        >
          <SuccessScreen
            headline={successHeadline}
            message={successMessage}
            icon={
              successIcon === undefined ? brandConfig.successIcon : successIcon
            }
          />
        </FormCard>
      </Nimbus3Scope>
    );
  }

  return (
    <Nimbus3Scope
      portalBridgeAttributes={portalBridgeAttributes}
      style={{ background: 'transparent' }}
    >
      {/* biome-ignore lint/nursery/noStaticElementInteractions: Analytics-only tracking, no state change */}
      <div
        ref={formRef}
        onMouseEnter={() => analytics.trackFormMouseover()}
        role="presentation"
      >
        <FormCard
          className={templateConfig.className}
          style={widthStyle}
          cardBackground={cardBackground}
          cardless={cardless}
        >
          {step === 0 && (
            <>
              <FormHeadline
                title={headline}
                subtitle={subHeading}
                showRuler={showRuler}
              />
              <Form {...emailForm}>
                <form
                  onSubmit={emailForm.handleSubmit(handleEmailSubmit, () =>
                    handleError('email')
                  )}
                  className="space-y-6"
                >
                  {isStacked ? (
                    <>
                      <div className="w-full">
                        <EmailField
                          onPointerDown={() =>
                            analytics.trackFormStarted('email-box-click')
                          }
                          onInput={() =>
                            analytics.trackFormStarted('email-box-type')
                          }
                        />
                      </div>
                      <FormFooter
                        lang={lang}
                        termsUrl={resolvedTermsUrl}
                        privacyUrl={resolvedPrivacyUrl}
                      />
                      <div className="flex justify-end">
                        <SubmitButton
                          text={buttonText}
                          isLoading={isLoading}
                          className={templateConfig.submitButtonStyle}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex gap-4 w-full flex-col md:flex-row md:items-start">
                        <div className="flex-1 w-full">
                          <EmailField
                            onPointerDown={() =>
                              analytics.trackFormStarted('email-box-click')
                            }
                            onInput={() =>
                              analytics.trackFormStarted('email-box-type')
                            }
                          />
                        </div>
                        <div className="md:mt-8 w-full md:w-auto">
                          <SubmitButton
                            text={buttonText}
                            isLoading={isLoading}
                            className={templateConfig.submitButtonStyle}
                          />
                        </div>
                      </div>
                      <FormFooter
                        lang={lang}
                        termsUrl={resolvedTermsUrl}
                        privacyUrl={resolvedPrivacyUrl}
                      />
                    </>
                  )}
                </form>
              </Form>
            </>
          )}

          {!isSingleStep && step === 1 && (
            <>
              <FormHeadline
                title={headline}
                subtitle={step2Subtitle}
                showRuler={true}
              />
              <DefaultQualificationStep
                form={qualificationForm}
                handleSubmit={qualificationForm.handleSubmit(
                  handleQualificationSubmit,
                  () => handleError('qualification')
                )}
                isLoading={isLoading}
                name={qualificationStepConfig.fieldName}
                buttonText={secondButtonText}
                label={qualificationStepConfig.label}
                options={qualificationStepConfig.options}
                buttonPlacement={isStacked ? 'stacked' : 'inline'}
                lang={lang}
              />
            </>
          )}
          <SubmissionErrorMessage message={submissionErrorMessage} />
        </FormCard>
      </div>
    </Nimbus3Scope>
  );
};
